import { useState, KeyboardEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { IconAdd } from '@/icons';
import { Flex } from '@/ui/Flex/Flex';
import { Modal } from '@/ui/Modal/Modal';

const StyledWrapper = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 40px;
  cursor: pointer;
  gap: 8px;
  padding: 4px 16px;
  border: 1px solid #000000;
  width: fit-content;
  height: 30px;
  background: #ffffff;
`;

const StyledIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: auto;
`;

const StyledButton = styled.button`
  padding: 10px 20px;
  background-color: #239ae7;
  color: #fff;
  border: none;
  border-radius: 4px;
  align-self: flex-end;
  cursor: pointer;
`;

const StyledInput = styled.input`
  width: 100%;
  margin-bottom: 50px;
  margin-top: 25px;
  border: 2px solid #b2b1ba;
  border-radius: 4px;
  padding: 10px;
`;
const StyledCheckbox = styled.label`
  display: flex;
  flex-wrap: wrap;
`;
const StyledCheckboxInput = styled.input`
  width: 15px;
  height: 15px;
  margin-left: 20px;
`;
const StyledCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 8px;
`;

type Props = {
  readonly href?: string;
  readonly className?: string;
  readonly columnName: string;
  readonly onSave: (name: string, isManaged?: boolean) => void;
};

export const AddButton = ({ href, className, columnName, onSave }: Props) => {
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDevManaged, setIsDevManaged] = useState<boolean>(false);

  const handleSave = () => {
    if (name.trim().length === 0) {
      return;
    }
    onSave(name);
    setEditMode(false);
    setIsModalOpen(false);
  };

  const handleCheckboxChange = () => {
    setIsDevManaged((prevOptions) => !prevOptions);
  };

  const handleModalClose = () => {
    setName('');
    setEditMode(false);
    setIsModalOpen(false);
    setIsDevManaged(false);
  };

  const saveValueAndReset = () => {
    if (name.trim().length === 0) {
      setEditMode(false);
      return;
    }

    onSave(name);
    handleModalClose();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | null>) => {
    if (event.key === 'Enter') {
      saveValueAndReset();
    }
  };

  let content = null;

  if (editMode && isModalOpen && columnName === 'Player') {
    content = (
      <Modal onClose={handleModalClose} title="Add Player">
        <StyledInput
          autoFocus
          onChange={(event) => setName(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="host name"
          type="text"
          value={name}
        />

        <StyledCheckbox>
          <StyledCheckboxWrapper>
            <FormattedMessage
              defaultMessage="Is Device Managed by Devs?"
              id="EegM9K"
            />
            <StyledCheckboxInput
              checked={isDevManaged}
              onChange={handleCheckboxChange}
              type="checkbox"
            />
          </StyledCheckboxWrapper>
        </StyledCheckbox>

        <StyledButton
          disabled={!name.trim()}
          onClick={() => {
            saveValueAndReset();
            onSave(name, isDevManaged);
          }}
        >
          <FormattedMessage defaultMessage="Save" id="jvo0vs" />
        </StyledButton>
      </Modal>
    );
  } else if (editMode && isModalOpen && columnName === 'Subsidiary') {
    content = (
      <Modal onClose={handleModalClose} title="Add subsidiary name">
        <StyledInput
          autoFocus
          onChange={(event) => setName(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="configuration name"
          type="text"
          value={name}
        />
        <StyledButton disabled={!name.trim()} onClick={handleSave}>
          <FormattedMessage defaultMessage="Save" id="jvo0vs" />
        </StyledButton>
      </Modal>
    );
  } else if (editMode && isModalOpen && columnName === 'Shelf') {
    content = (
      <Modal onClose={handleModalClose} title="Add Shelf">
        <StyledInput
          autoFocus
          onChange={(event) => setName(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="shelf name"
          type="text"
          value={name}
        />
        <StyledButton disabled={!name.trim()} onClick={handleSave}>
          <FormattedMessage defaultMessage="Save" id="jvo0vs" />
        </StyledButton>
      </Modal>
    );
  }

  return (
    <Flex direction="column" gap="8px 0px">
      {content}
      <StyledWrapper
        as={typeof href === 'string' ? NavLink : undefined}
        className={className}
        onClick={() => {
          setEditMode(true);
          setIsModalOpen(true);
        }}
        to={href!}
      >
        <StyledIcon>
          <IconAdd />
        </StyledIcon>
        <span>
          <FormattedMessage defaultMessage="Add" id="2/2yg+" />
        </span>
      </StyledWrapper>
    </Flex>
  );
};
